import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import autobind from 'core-decorators/lib/autobind';

import VIDEO_PLAYBACK_STATUSES from '@wix/wix-vod-shared/src/common/constants/video-playback-status';
import { isExternalVideo } from '@wix/wix-vod-shared/src/common/ui-selectors/video/video-origin';
import NotAvailableOverlay from './components/not-available-overlay/not-available-overlay';
import { VODCSSModules } from 'shared/utils/wix-connect';
import ResizedImage from 'shared/components/resized-image/resized-image';

import { MAIN_ITEM_TYPE_VALUES } from '@wix/wix-vod-shared/src/app-settings/constants/main-item-type-values';

import { setPlaybackStatus } from 'widget/redux/client/actions/player/change-playback-status';
import { setFullScreenStatus } from 'widget/redux/client/actions/player/set-fullscreen-status';
import {
  isVideoPausedOptimistic,
  isVideoPlayingOptimistic,
  isVideoPlayRequested,
  isVideoPauseRequested,
  isVideoPlaying,
  isVideoPaused,
} from 'widget/selectors/video-playback-status';
import { isInFullScreen } from 'shared/selectors/player';
import focus from '@wix/wix-vod-shared/src/widget/utils/accessibility-focus';

import ExternalPlayer from './player-external';
import InternalPlayer from './player-internal';
import ChannelCover from 'widget/containers/channel-cover/channel-cover';

import styles from './player.styl';


@connect(
  (state, ownProps) => ({
    isVideoPlayRequested: isVideoPlayRequested(state),
    isVideoPauseRequested: isVideoPauseRequested(state),
    isVideoPlaying: isVideoPlaying(state),
    isVideoPaused: isVideoPaused(state),
    isVideoPlayingOptimistic: isVideoPlayingOptimistic(state),
    isVideoPausedOptimistic: isVideoPausedOptimistic(state),
    isExternalVideo: isExternalVideo(ownProps.videoItem),
    isInFullScreen: isInFullScreen(state),
  }), {
    setPlaybackStatus,
    setFullScreenStatus,
  }
)
@VODCSSModules(styles)
export default class Player extends React.Component {

  static propTypes = {
    appSettings: PropTypes.object,
    channelData: PropTypes.object,
    videoItem: PropTypes.object.isRequired,

    canShowChannelCover: PropTypes.bool,

    isVideoPlayRequested: PropTypes.bool,
    isVideoPauseRequested: PropTypes.bool,
    isVideoPlaying: PropTypes.bool,
    isVideoPaused: PropTypes.bool,
    isVideoPlayingOptimistic: PropTypes.bool,
    isVideoPausedOptimistic: PropTypes.bool,
    isInFullScreen: PropTypes.bool,

    showPoster: PropTypes.bool,
    simple: PropTypes.bool,
    mobileMode: PropTypes.bool,
    isExternalVideo: PropTypes.bool,

    children: PropTypes.any,

    width: PropTypes.number,
    height: PropTypes.number,

    onPlayStart: PropTypes.func,
    onResume: PropTypes.func,
    onEnded: PropTypes.func,
    onFullScreenChanged: PropTypes.func,

    setPlaybackStatus: PropTypes.func,
    setFullScreenStatus: PropTypes.func,

    videoUrls: PropTypes.object,
    isOverQuota: PropTypes.bool,
  };

  static defaultProps = {
    onPlayStart: _.noop,
    onResume: _.noop,
    onEnded: _.noop,

    showPoster: true,
  };

  constructor(props) {
    super(props);

    this.playerWrapperRef = null;
    this.iframeRef = null;
    this.state = {
      beenPlayed: false,
      ended: false,
    };
  }

  componentWillReceiveProps(newProps) {
    if (this.isVideoIdChanged(newProps)) {
      this.setState({ beenPlayed: false, ended: false });
    }
  }

  componentDidUpdate(prevProps) {
    const { isVideoPlaying, isExternalVideo } = this.props;
    const isVideoPlayingChanged = prevProps.isVideoPlaying !== isVideoPlaying;

    if (isVideoPlayingChanged && isVideoPlaying) {
      focus(isExternalVideo ? this.iframeRef : this.playerWrapperRef);
    }
  }

  isVideoIdChanged(newProps) {
    return newProps.videoItem.id !== this.props.videoItem.id;
  }

  @autobind
  saveIframeRef(ref) {
    this.iframeRef = ref;
  }

  @autobind
  savePlayerWrapperRef(ref) {
    this.playerWrapperRef = ref;
  }

  @autobind
  setPlayStatus() {
    const { setPlaybackStatus, isVideoPlaying } = this.props;

    if (!isVideoPlaying) {
      const { beenPlayed } = this.state;
      if (beenPlayed) {
        this.onResume();
      } else {
        this.onPlayStart();
      }

      setPlaybackStatus(VIDEO_PLAYBACK_STATUSES.PLAYING);
    }
  }

  @autobind
  setPauseStatus() {
    const { setPlaybackStatus, isVideoPaused } = this.props;
    if (!isVideoPaused) {
      setPlaybackStatus(VIDEO_PLAYBACK_STATUSES.PAUSED);
    }
  }

  @autobind
  setEndStatus() {
    const { setPlaybackStatus } = this.props;
    this.onEnded();
    setPlaybackStatus(VIDEO_PLAYBACK_STATUSES.ENDED);
  }

  get showChannelCover() {
    const { appSettings, canShowChannelCover } = this.props;
    return canShowChannelCover && appSettings.numbers.mainItemType === MAIN_ITEM_TYPE_VALUES.CHANNEL_COVER;
  }

  showVideoTitle() {
    return _.get(this.props, 'appSettings.booleans.showVideoTitle', true);
  }

  shouldShowInitialOverlay() {
    const { isVideoPlayingOptimistic, isVideoPausedOptimistic, mobileMode, isInFullScreen } = this.props;

    if (mobileMode) {
      return isInFullScreen ? false : !isVideoPlayingOptimistic;
    }

    return !isVideoPlayingOptimistic && !isVideoPausedOptimistic;
  }

  get posterUrl() {
    const { videoItem, channelData } = this.props;

    const { customCoverUrl } = videoItem;
    let { coverUrl } = videoItem;

    coverUrl = customCoverUrl || coverUrl;

    if (this.showChannelCover) {
      coverUrl = channelData.customCoverUrl || channelData.coverUrl || coverUrl;
    }

    return coverUrl;
  }

  get initialOverlay() {
    const { children } = this.props;
    const { beenPlayed, ended } = this.state;

    if (!children) {
      return null;
    }

    const { channelData } = this.props;
    const originProps = _.pick(this.props, 'showChannelCover', 'appSettings', 'videoItem');

    return React.cloneElement(children, _.assign({
      channelData,
      beenPlayed,
      ended,
      showChannelCover: this.showChannelCover,
    }, originProps));
  }

  get poster() {
    const {
      simple,
      isExternalVideo,
      showPoster,
      width,
      height,
    } = this.props;

    if (!showPoster) {
      return null;
    }

    if (simple && isExternalVideo) {
      return null;
    }

    const posterUrl = this.posterUrl;

    if (!posterUrl) {
      return null;
    }

    const { channelData } = this.props;

    // TODO: use separated video players for dashboard and widget !!!
    if (channelData) {
      return (
        <div className={styles['cover-image']}>
          <ChannelCover posterUrl={posterUrl}
                        mediaInfo={channelData.mediaInfo}
                        channelId={channelData.id}
                        showVideoCover={this.showChannelCover}
                        width={width}
                        height={height} />
        </div>
      );
    }

    return (
      <ResizedImage styleName="cover-image"
                    src={posterUrl}
                    width={width}
                    height={height} />

    );
  }

  get overlay() {
    if (!this.shouldShowInitialOverlay()) {
      return null;
    }

    return (
      <div styleName="overlay"
           style={this.styles}>
        {this.initialOverlay}
        {this.poster}
      </div>
    );
  }

  get styles() {
    const { width, height } = this.props;
    return {
      width,
      height,
    };
  }

  @autobind
  onPlayStart() {
    this.props.onPlayStart();
    this.setState({ beenPlayed: true });
  }

  @autobind
  onEnded() {
    this.props.onEnded();
    this.setState({ ended: true, beenPlayed: false });
  }

  @autobind
  onResume() {
    this.props.onResume();
    this.setState({ ended: false });
  }

  @autobind
  onFullScreenChanged(isInFullScreen) {
    const { setFullScreenStatus, onFullScreenChanged } = this.props;

    setFullScreenStatus(isInFullScreen);
    onFullScreenChanged(isInFullScreen);
  }

  get player() {
    const { isOverQuota, width, height } = this.props;
    const props = _.omit(this.props, 'intl', 'styles');

    // NOTE: we need to put share overlay inside VideoPlayer
    // to be sure player would be shown in full screen mode
    if (isOverQuota) {
      return (<NotAvailableOverlay width={width}
                                   height={height} />);
    }

    if (this.props.isExternalVideo) {
      return (
        <ExternalPlayer {...props}
                        onFullScreenChanged={this.onFullScreenChanged}
                        onPlay={this.setPlayStatus}
                        onPause={this.setPauseStatus}
                        onEnded={this.setEndStatus}
                        onIframeRef={this.saveIframeRef} />
      );
    }

    return (
      <InternalPlayer {...props}
                      canShowTitle={this.showVideoTitle()}
                      onFullScreenChanged={this.onFullScreenChanged}
                      onPlay={this.setPlayStatus}
                      onPause={this.setPauseStatus}
                      onEnded={this.setEndStatus} />
    );
  }

  render() {
    return (
      <div styleName="player"
           data-hook="player"
           style={this.styles}>
        {this.overlay}
        <div styleName="player-wrapper"
             ref={this.savePlayerWrapperRef}
             aria-hidden={this.shouldShowInitialOverlay()}>
          {this.player}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/new-player/player.js